import { notification } from 'antd';
import axios, { AxiosProgressEvent, AxiosRequestConfig } from 'axios';
import qs from 'qs';
import React, { DependencyList, useEffect, useState } from 'react';
import { handleStatus } from 'src/api/sh-api';
import { UpdateProgressFunc } from 'src/progress-promise';

export function uploadProgressHandler(message: string, updateFunc: UpdateProgressFunc) {
  const bytesToMB = (bytes: number) => (bytes / 1024 / 1024).toFixed(1);
  return (e: AxiosProgressEvent) => updateFunc(`${message} (${bytesToMB(e.loaded)} / ${bytesToMB(e.total!)} MB)`, e.progress! * 100);
}

export async function apiFetchUpload<T>(url: string, config: Exclude<AxiosRequestConfig, 'url'>) {
  // Useful default headers are already provided as global defaults elsewhere.
  const res = await axios.request<MaybeApiResult<T>>({ url: `${window.rootDirectory}api/${url}`, ...config }).then(handleStatus);
  if (isResult<T>(res.data)) {
    return res.data.result;
  }
  handleError(new Error(res.data.message.body ? `${res.data.message.title}: ${res.data.message.body}` : res.data.message.title));
}

type ApiResult<T> = { result: T };
type ApiFailure = { errorType: string; message: { title: string; body?: string } };
type MaybeApiResult<T> = ApiResult<T> | ApiFailure;

function isResult<T>(result: MaybeApiResult<T>): result is ApiResult<T> {
  return !!(result as ApiResult<T>).result;
}

async function parseJson<T>(response?: Response) {
  try {
    return response && ((await response.json()) as T);
  } catch (_e) {
    // Response isn't JSON. We do this when not returning anything sometimes.
    return undefined;
  }
}

function handleError(err: unknown, showErrorMessage?: string): never {
  if (showErrorMessage) {
    console.error({ err });
    notification.error({
      message: (
        <>
          <div>{showErrorMessage}</div>
          {err instanceof Error && (
            <code>
              {err.name}: {err.message}
            </code>
          )}
        </>
      ),
    });
  }
  throw err;
}

export async function apiFetch<T = undefined>(
  query: string,
  init?: RequestInit,
  showErrorMessage?: string,
  handle404AsUndefined = false,
): Promise<T> {
  try {
    const res = await fetch(`${window.rootDirectory}api/${query}`, {
      ...init,
      headers: { ...init?.headers, 'X-Requested-With': 'XMLHttpRequest' },
    }).then((r) => handleStatus(r, handle404AsUndefined));
    if (handle404AsUndefined && res.status === 404) {
      return undefined as T;
    }
    const res_1 = await parseJson<MaybeApiResult<T>>(res);
    if (!res_1) {
      return undefined as T;
    }
    if (isResult(res_1)) {
      return res_1.result;
    }
    handleError(new Error(res_1.message.body ? `${res_1.message.title}: ${res_1.message.body}` : res_1.message.title), showErrorMessage);
  } catch (e) {
    handleError(e, showErrorMessage);
  }
}

export async function fetchOne<T>(
  query: string,
  setData: (data: T | undefined) => void,
  setLoading: (loading: boolean) => void,
  fetchErrorMessage: string,
  onComplete?: () => void,
  handle404AsUndefined = false,
) {
  setLoading(true);
  try {
    setData(await apiFetch<T>(query, undefined, fetchErrorMessage, handle404AsUndefined));
  } catch (_err) {
    setData(undefined);
  } finally {
    setLoading(false);
    onComplete?.();
  }
}

export async function fetchData<T>(
  query: string,
  setData: (data: T[]) => void,
  setLoading: (loading: boolean) => void,
  fetchErrorMessage: string,
  onComplete?: () => void,
) {
  return await fetchOne<T[]>(query, (data) => setData(data ?? []), setLoading, fetchErrorMessage, onComplete);
}

export type UseFetchData<T> = [T[] | undefined, boolean, () => Promise<void>];

/**
 * Fetch data asynchronously in a functional React component.
 * @param url The URL to query
 * @param errorMessage Error message to display in a notification if an error occurs
 * @param queryParams Query parameters to pass in - changes to these cause the query to be re-run
 * @param extraDeps Other dependencies for the hook - changes to these cause the query to be re-run
 * @param onComplete Callback to call when a query completes
 * @returns An array of:
 * - the list of results (or `undefined` if not complete yet)
 * - a boolean for if the query is currently in progress
 * - a function to call to re-run the query (which returns a `Promise`)
 */
export function useFetchData<T>(
  url: string | undefined,
  errorMessage: string,
  queryParams?: { [key: string]: string | boolean | undefined },
  extraDeps?: DependencyList,
  onComplete?: () => void,
): UseFetchData<T> {
  const [data, setData] = useState<T[]>();
  const [loading, setLoading] = useState(false);

  const query = queryParams ? `?${qs.stringify(queryParams)}` : '';
  const fetchDataFunc = () => fetchData(`${url}${query}`, setData, setLoading, errorMessage, onComplete);
  useEffect(() => {
    if (url) {
      fetchDataFunc();
    }
  }, [url, ...Object.values(queryParams || {}), ...(extraDeps || [])]);

  return [data, loading, fetchDataFunc];
}

export type UseFetchOne<T> = [T | undefined, boolean, () => Promise<void>];

/**
 * Fetch one thing as a hook
 * @param url The API URL - use undefined to stall fetching
 * @param errorMessage Error to display if the request fails
 * @param queryParams Additional query parameters to request - changing these will refetch
 * @param extraDeps Additional dependencies to cause a refetch
 * @param onComplete Callback for when the request is completed
 * @param handle404AsUndefined Return `undefined` for a 404, rather than throwing an exception
 * @param shouldFetch If we should go through with the fetch
 */
export function useFetchOne<T>(
  url: string | undefined,
  errorMessage: string,
  queryParams?: { [key: string]: string | boolean | undefined },
  extraDeps?: DependencyList,
  onComplete?: () => void,
  handle404AsUndefined = false,
  shouldFetch = true,
): UseFetchOne<T> {
  const [data, setData] = useState<T>();
  const [loading, setLoading] = useState(false);

  const query = queryParams ? `?${qs.stringify(queryParams)}` : '';
  const fetchDataFunc = () => fetchOne(`${url}${query}`, setData, setLoading, errorMessage, onComplete, handle404AsUndefined);
  useEffect(() => {
    if (url && shouldFetch) {
      fetchDataFunc();
    } else {
      setData(undefined);
    }
  }, [url, ...Object.values(queryParams || {}), ...(extraDeps || [])]);

  return [data, loading, fetchDataFunc];
}
